import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../../../../redux/hooks';
import { QUERY_KEY } from '../../../../../services/queryEndPoint';
import { fetchUserProfile } from '../../../../profile/api/userProfileApi';
import { fetchWaiver } from '../../api/waiverApi';
import { signTournamentWaiver, type IWaiverSignature, type SignatureType } from './waiverSignatureApi';
import { exportSignedWaiverToPDF } from './waiverPdfExport';
import SignaturePad, { type SignaturePadHandle } from './SignaturePad';
import '../../../../myLeagues/CreateLeague.scss';
import '../../../waiver/CreateWaiverPage.scss';
import './MyWaiverTab.scss';

interface Props {
  tournamentId: string;
  tournamentName: string | null;
  leagueId: string | null;
  waiverId: string | null;
  teamName: string | null;
  onClose: () => void;
  onSigned: () => void;
}

const RELATIONSHIP_OPTIONS = ['Parent', 'Guardian', 'Spouse', 'Sibling', 'Friend', 'Other'];

const WIZARD_STEPS = [
  { id: 1, label: 'Confirm your details' },
  { id: 2, label: 'Agreement and Signature' },
] as const;

function calculateAge(dobIso: string): number | null {
  if (!dobIso) return null;
  const dob = new Date(dobIso);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age;
}

export default function CompleteWaiverWizard({
  tournamentId,
  tournamentName,
  leagueId,
  waiverId,
  teamName,
  onClose,
  onSigned,
}: Props) {
  const [step, setStep] = useState<1 | 2 | 'success'>(1);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [signatureMode, setSignatureMode] = useState<SignatureType>('TYPED');
  const [typedSignature, setTypedSignature] = useState('');
  const signaturePadRef = useRef<SignaturePadHandle>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [parentGuardianName, setParentGuardianName] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [signedResult, setSignedResult] = useState<IWaiverSignature | null>(null);
  const [seeded, setSeeded] = useState(false);

  const cachedProfile = useAppSelector((s) => s.selectedProfile.playerProfile);
  const { data: fetchedProfile } = useQuery({
    queryKey: ['myWaiverProfile'],
    queryFn: fetchUserProfile,
    enabled: !cachedProfile,
  });
  const profile = cachedProfile ?? fetchedProfile ?? null;
  const player = profile?.playerResponse ?? null;

  const { data: waiver, isLoading: isLoadingWaiver } = useQuery({
    queryKey: [QUERY_KEY.leagueWaivers, leagueId, 'waiver', waiverId],
    queryFn: () => fetchWaiver(leagueId as string, waiverId as string),
    enabled: !!leagueId && !!waiverId,
  });

  // Seed field values from the player's profile once, when both are ready.
  useEffect(() => {
    if (seeded || !waiver || !player) return;
    const seed: Record<string, string> = {};
    const fullName = `${player.playerFirstName ?? ''} ${player.playerLastName ?? ''}`.trim();
    for (const field of waiver.playerInfoFields ?? []) {
      if (field === 'FULL_NAME') seed.FULL_NAME = fullName;
      else if (field === 'DATE_OF_BIRTH') seed.DATE_OF_BIRTH = player.dob ? player.dob.slice(0, 10) : '';
      else if (field === 'EMAIL') seed.EMAIL = player.emailId ?? '';
      else if (field === 'PHONE_NUMBER') seed.PHONE_NUMBER = player.phoneNo ?? '';
      else if (field === 'ADDRESS') {
        seed.ADDRESS = [player.stAddress, player.city, player.state, player.zipCode]
          .filter(Boolean)
          .join(', ');
      } else if (field === 'EMERGENCY_CONTACT') {
        seed.EMERGENCY_CONTACT_NAME = player.emergencyName ?? '';
        seed.EMERGENCY_CONTACT_PHONE = player.emergencyNo ?? '';
        seed.EMERGENCY_CONTACT_RELATIONSHIP = '';
      }
    }
    setFieldValues(seed);
    // Deliberately not pre-filling the signature itself (unlike the profile fields above) — an
    // e-signature should be something the player actively types, not a name that just appears
    // because it happens to match their profile.
    setSeeded(true);
  }, [seeded, waiver, player]);

  const signerAge = fieldValues.DATE_OF_BIRTH ? calculateAge(fieldValues.DATE_OF_BIRTH) : null;
  const signerIsMinor = signerAge != null && signerAge < 18;

  const setField = (key: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validateStep1 = (): boolean => {
    if (!waiver) return false;
    const nextErrors: Record<string, string> = {};
    for (const field of waiver.playerInfoFields ?? []) {
      if (field === 'MEDICAL_CONDITIONS_DISCLOSURE') continue;
      if (field === 'EMERGENCY_CONTACT') {
        if (!fieldValues.EMERGENCY_CONTACT_NAME?.trim()) nextErrors.EMERGENCY_CONTACT_NAME = 'Required';
        if (!fieldValues.EMERGENCY_CONTACT_PHONE?.trim()) nextErrors.EMERGENCY_CONTACT_PHONE = 'Required';
        if (!fieldValues.EMERGENCY_CONTACT_RELATIONSHIP?.trim()) nextErrors.EMERGENCY_CONTACT_RELATIONSHIP = 'Required';
      } else if (!fieldValues[field]?.trim()) {
        nextErrors[field] = 'Required';
      }
    }
    for (const customField of waiver.customFields ?? []) {
      if (!fieldValues[customField]?.trim()) nextErrors[customField] = 'Required';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const signMutation = useMutation({
    mutationFn: () => {
      if (!waiver) throw new Error('Waiver not loaded.');
      const isDrawing = waiver.requireDigitalSignature && signatureMode === 'DRAWN';
      return signTournamentWaiver(tournamentId, {
        agreedToTerms,
        signatureType: waiver.requireDigitalSignature ? signatureMode : undefined,
        digitalSignature: waiver.requireDigitalSignature && !isDrawing ? typedSignature.trim() : undefined,
        digitalSignatureImage: isDrawing ? signaturePadRef.current?.getDataUrl() ?? undefined : undefined,
        dateConfirmed: waiver.requireDateConfirmation
          ? new Date().toISOString().slice(0, 10)
          : undefined,
        signerIsMinor,
        parentGuardianName: signerIsMinor ? parentGuardianName.trim() : undefined,
        fieldValues,
      });
    },
    onSuccess: (result) => {
      setSignedResult(result);
      setStep('success');
    },
    onError: (error: unknown) => {
      const ax = error as { response?: { data?: { message?: string } } };
      toast.error(ax?.response?.data?.message || 'Failed to submit waiver. Please try again.');
    },
  });

  const handleSubmit = () => {
    if (!waiver) return;
    if (waiver.requireAgreeCheckbox && !agreedToTerms) {
      toast.error('Please agree to the waiver terms.');
      return;
    }
    if (waiver.requireDigitalSignature) {
      if (signatureMode === 'TYPED' && !typedSignature.trim()) {
        toast.error('Please type your name to sign.');
        return;
      }
      if (signatureMode === 'DRAWN' && (signaturePadRef.current?.isEmpty() ?? true)) {
        toast.error('Please draw your signature.');
        return;
      }
    }
    if (waiver.requireParentGuardianIfMinor && signerIsMinor && !parentGuardianName.trim()) {
      toast.error("Please provide your parent/guardian's name.");
      return;
    }
    signMutation.mutate();
  };

  const renderedFields = useMemo(() => {
    if (!waiver) return [];
    return (waiver.playerInfoFields ?? []).filter((f) => f !== 'MEDICAL_CONDITIONS_DISCLOSURE');
  }, [waiver]);

  const customFields = waiver?.customFields ?? [];

  if (isLoadingWaiver || !waiver) {
    return (
      <div className="complete-waiver-wizard">
        <p>Loading waiver…</p>
      </div>
    );
  }

  if (step === 'success' && signedResult) {
    return (
      <div className="complete-waiver-wizard">
        <div className="complete-waiver-success">
          <div className="complete-waiver-success-icon" aria-hidden>
            ✓
          </div>
          <h2>Waiver submitted and stored</h2>
          <p>
            {player?.emailId
              ? `A copy has been emailed to ${player.emailId}. You're available for selection.`
              : "You're available for selection."}
          </p>

          <div className="complete-waiver-summary">
            <div>
              <span>Waiver</span>
              <strong>{waiver.name}</strong>
            </div>
            <div>
              <span>Tournament</span>
              <strong>{tournamentName ?? '-'}</strong>
            </div>
            <div>
              <span>Team</span>
              <strong>{teamName ?? '-'}</strong>
            </div>
            <div>
              <span>Signed by</span>
              {signedResult.signatureType === 'DRAWN' && signedResult.digitalSignatureUrl ? (
                <img
                  src={signedResult.digitalSignatureUrl}
                  alt="Your signature"
                  className="complete-waiver-signature-preview"
                />
              ) : (
                <strong>{signedResult.digitalSignature || typedSignature || '-'}</strong>
              )}
            </div>
            <div>
              <span>Signed on</span>
              <strong>
                {signedResult.signedAt ? new Date(signedResult.signedAt).toLocaleDateString() : '-'}
              </strong>
            </div>
            {waiver.expiryDate && (
              <div>
                <span>Valid until</span>
                <strong>{waiver.expiryDate}</strong>
              </div>
            )}
          </div>

          <div className="complete-waiver-success-actions">
            <button
              type="button"
              className="waiver-btn waiver-btn--secondary"
              onClick={() =>
                exportSignedWaiverToPDF({
                  waiverName: waiver.name,
                  tournamentName: tournamentName ?? '-',
                  teamName: teamName ?? '-',
                  signedBy: signedResult.digitalSignature || typedSignature || '-',
                  signedOn: signedResult.signedAt ? new Date(signedResult.signedAt).toLocaleDateString() : '-',
                  agreementContent: waiver.agreementContent,
                  signatureType: signedResult.signatureType,
                  signatureImageUrl: signedResult.digitalSignatureUrl,
                  agreeCheckboxLabel: waiver.agreeCheckboxLabel,
                  agreedToTerms: signedResult.agreedToTerms,
                  signerIsMinor,
                  parentGuardianName: signedResult.parentGuardianName,
                  playerInfoFields: waiver.playerInfoFields,
                  customFields: waiver.customFields,
                  fieldValues: signedResult.fieldValues,
                })
              }
            >
              Download PDF
            </button>
            <button type="button" className="waiver-btn waiver-btn--primary" onClick={onSigned}>
              Back to my team
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="complete-waiver-wizard">
      <button type="button" className="complete-waiver-back" onClick={onClose}>
        <span aria-hidden>←</span> Complete your Waiver
      </button>

      <div className="create-league-stepper-wrap">
        <ol className="create-league-stepper" role="list">
          {WIZARD_STEPS.map((s, index) => {
            const isActive = step === s.id;
            const isCompleted = typeof step === 'number' && step > s.id;
            return (
              <li key={s.id} className={`create-league-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                {index > 0 && (
                  <span className={`create-league-step-connector ${typeof step === 'number' && step >= s.id ? 'dark' : ''}`} aria-hidden />
                )}
                <span className={`create-league-step-dot ${isActive || isCompleted ? 'reached' : ''}`}>
                  <span className="create-league-step-dot-inner">{s.id}</span>
                </span>
                <span className="create-league-step-label">{s.label}</span>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="complete-waiver-meta">
        <div>
          <span>Tournament</span>
          <strong>{tournamentName ?? '-'}</strong>
        </div>
        <div>
          <span>Team</span>
          <strong>{teamName ?? '-'}</strong>
        </div>
        <div>
          <span>Waiver</span>
          <strong>{waiver.name}</strong>
        </div>
        <div>
          <span>Valid until</span>
          <strong>{waiver.expiryDate || 'End of season'}</strong>
        </div>
      </div>

      {step === 1 && (
        <div className="complete-waiver-step">
          <h3>Confirm your details</h3>

          <div className="complete-waiver-field-grid">
            {renderedFields.includes('FULL_NAME') && (
              <div className="waiver-settings-form-group">
                <label>Full Name*</label>
                <input
                  type="text"
                  value={fieldValues.FULL_NAME ?? ''}
                  onChange={(e) => setField('FULL_NAME', e.target.value)}
                  className={errors.FULL_NAME ? 'field-error' : ''}
                />
                {errors.FULL_NAME && <p className="err-msg">{errors.FULL_NAME}</p>}
              </div>
            )}
            {renderedFields.includes('DATE_OF_BIRTH') && (
              <div className="waiver-settings-form-group">
                <label>Date of Birth*</label>
                <input
                  type="date"
                  value={fieldValues.DATE_OF_BIRTH ?? ''}
                  onChange={(e) => setField('DATE_OF_BIRTH', e.target.value)}
                  className={errors.DATE_OF_BIRTH ? 'field-error' : ''}
                />
                <p className="complete-waiver-hint">A guardian co-signs if you're under 18.</p>
                {errors.DATE_OF_BIRTH && <p className="err-msg">{errors.DATE_OF_BIRTH}</p>}
              </div>
            )}
            {renderedFields.includes('EMAIL') && (
              <div className="waiver-settings-form-group">
                <label>Email*</label>
                <input
                  type="text"
                  value={fieldValues.EMAIL ?? ''}
                  onChange={(e) => setField('EMAIL', e.target.value)}
                  className={errors.EMAIL ? 'field-error' : ''}
                />
                {!player?.emailId && (
                  <p className="complete-waiver-hint">No email on file for your account — please add one.</p>
                )}
                {errors.EMAIL && <p className="err-msg">{errors.EMAIL}</p>}
              </div>
            )}
            {renderedFields.includes('PHONE_NUMBER') && (
              <div className="waiver-settings-form-group">
                <label>Phone Number*</label>
                <input
                  type="text"
                  value={fieldValues.PHONE_NUMBER ?? ''}
                  onChange={(e) => setField('PHONE_NUMBER', e.target.value)}
                  className={errors.PHONE_NUMBER ? 'field-error' : ''}
                />
                {errors.PHONE_NUMBER && <p className="err-msg">{errors.PHONE_NUMBER}</p>}
              </div>
            )}
            {renderedFields.includes('ADDRESS') && (
              <div className="waiver-settings-form-group complete-waiver-field-wide">
                <label>Address*</label>
                <input
                  type="text"
                  value={fieldValues.ADDRESS ?? ''}
                  onChange={(e) => setField('ADDRESS', e.target.value)}
                  className={errors.ADDRESS ? 'field-error' : ''}
                />
                {errors.ADDRESS && <p className="err-msg">{errors.ADDRESS}</p>}
              </div>
            )}
          </div>

          {renderedFields.includes('EMERGENCY_CONTACT') && (
            <div className="complete-waiver-emergency">
              <p className="complete-waiver-section-label">Emergency Contact</p>
              <div className="complete-waiver-field-grid">
                <div className="waiver-settings-form-group">
                  <label>Name*</label>
                  <input
                    type="text"
                    placeholder="Full name"
                    value={fieldValues.EMERGENCY_CONTACT_NAME ?? ''}
                    onChange={(e) => setField('EMERGENCY_CONTACT_NAME', e.target.value)}
                    className={errors.EMERGENCY_CONTACT_NAME ? 'field-error' : ''}
                  />
                  {errors.EMERGENCY_CONTACT_NAME && <p className="err-msg">{errors.EMERGENCY_CONTACT_NAME}</p>}
                </div>
                <div className="waiver-settings-form-group">
                  <label>Phone*</label>
                  <input
                    type="text"
                    placeholder="Phone number"
                    value={fieldValues.EMERGENCY_CONTACT_PHONE ?? ''}
                    onChange={(e) => setField('EMERGENCY_CONTACT_PHONE', e.target.value)}
                    className={errors.EMERGENCY_CONTACT_PHONE ? 'field-error' : ''}
                  />
                  {errors.EMERGENCY_CONTACT_PHONE && <p className="err-msg">{errors.EMERGENCY_CONTACT_PHONE}</p>}
                </div>
                <div className="waiver-settings-form-group">
                  <label>Relationship*</label>
                  <select
                    value={fieldValues.EMERGENCY_CONTACT_RELATIONSHIP ?? ''}
                    onChange={(e) => setField('EMERGENCY_CONTACT_RELATIONSHIP', e.target.value)}
                    className={errors.EMERGENCY_CONTACT_RELATIONSHIP ? 'field-error' : ''}
                  >
                    <option value="" disabled>
                      Select
                    </option>
                    {RELATIONSHIP_OPTIONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  {errors.EMERGENCY_CONTACT_RELATIONSHIP && (
                    <p className="err-msg">{errors.EMERGENCY_CONTACT_RELATIONSHIP}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {customFields.length > 0 && (
            <div className="complete-waiver-field-grid">
              {customFields.map((customField) => (
                <div className="waiver-settings-form-group" key={customField}>
                  <label>{customField}*</label>
                  <input
                    type="text"
                    value={fieldValues[customField] ?? ''}
                    onChange={(e) => setField(customField, e.target.value)}
                    className={errors[customField] ? 'field-error' : ''}
                  />
                  {errors[customField] && <p className="err-msg">{errors[customField]}</p>}
                </div>
              ))}
            </div>
          )}

          <div className="complete-waiver-disclosure">
            <strong>What the league does and doesn't collect</strong>
            <p>
              Your name, contact details and emergency contact are stored. No medical conditions,
              medications, physician or insurance details are collected or stored anywhere in
              CricCenter. In an emergency, match officials contact the person you list above.
            </p>
          </div>

          <div className="complete-waiver-actions">
            <button
              type="button"
              className="waiver-btn waiver-btn--primary"
              onClick={() => {
                if (validateStep1()) setStep(2);
              }}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="complete-waiver-step">
          <h3>Waiver agreement</h3>
          <div
            className="complete-waiver-agreement"
            // Trusted content: authored by the league's own admin via the waiver rich-text editor.
            dangerouslySetInnerHTML={{ __html: waiver.agreementContent }}
          />

          {waiver.requireAgreeCheckbox && (
            <label className="waiver-checkbox complete-waiver-agree">
              <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} />
              {waiver.agreeCheckboxLabel || 'I have read and agree to this waiver, and I agree to sign it electronically.'}
            </label>
          )}

          {waiver.requireParentGuardianIfMinor && signerIsMinor && (
            <div className="waiver-settings-form-group complete-waiver-guardian">
              <label>Parent/Guardian Name*</label>
              <input
                type="text"
                value={parentGuardianName}
                onChange={(e) => setParentGuardianName(e.target.value)}
                placeholder="Full name of parent or guardian"
              />
            </div>
          )}

          {waiver.requireDigitalSignature && (
            <div className="complete-waiver-signature">
              <p className="complete-waiver-section-label">Signature</p>
              <div className="complete-waiver-signature-tabs">
                <button
                  type="button"
                  className={signatureMode === 'TYPED' ? 'active' : ''}
                  onClick={() => setSignatureMode('TYPED')}
                >
                  Type
                </button>
                <button
                  type="button"
                  className={signatureMode === 'DRAWN' ? 'active' : ''}
                  onClick={() => setSignatureMode('DRAWN')}
                >
                  Draw
                </button>
              </div>
              {signatureMode === 'TYPED' ? (
                <>
                  <label>Type your full name to sign*</label>
                  <input
                    type="text"
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                    placeholder="Your full name"
                  />
                  <p className="complete-waiver-hint">
                    This is your electronic signature — please type it yourself rather than copying
                    it from elsewhere.
                  </p>
                </>
              ) : (
                <>
                  <SignaturePad ref={signaturePadRef} />
                  <div className="complete-waiver-signature-pad-actions">
                    <button
                      type="button"
                      className="complete-waiver-use-profile-name"
                      onClick={() => signaturePadRef.current?.clear()}
                    >
                      Clear
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="complete-waiver-actions complete-waiver-actions--split">
            <button type="button" className="waiver-btn waiver-btn--text" onClick={() => setStep(1)}>
              Back
            </button>
            <button
              type="button"
              className="waiver-btn waiver-btn--primary"
              onClick={handleSubmit}
              disabled={signMutation.isPending}
            >
              {signMutation.isPending ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
